import React from "react";

import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorPortfolioAccount } from "../../../Types/XFactorViewsRendering";
import AccountsPortfolio from "../components/AccountsPortfolio";
import AccountSynopsis from "../components/AccountSynopsis";
import BriefingCardsModal from "../components/BriefingCardsModal";
import BubbleMatrixModal from "../components/BubbleMatrixModal";
import { ACCENT, PartialStatus, StatusBadge } from "../components/Chrome";
import ExecProfilesModal from "../components/ExecProfilesModal";
import ExecutiveBrief from "../components/ExecutiveBrief";
import ExplainabilityModal from "../components/ExplainabilityModal";
import FullBriefingModal from "../components/FullBriefingModal";
import RoChartModal from "../components/RoChartModal";
import SectionDetailModal from "../components/SectionDetailModal";
import SituationCard from "../components/SituationCard";
import { formatDate, unmappedKeys } from "../lib/derive";

const STEPS = [
  "Situation Card",
  "Executive Brief",
  "Account Synopsis",
] as const;

/**
 * The stitched end-to-end experience: portfolio → three-step account flow, with
 * every briefing artifact reachable as a modal.
 *
 * Public Data only. xfactor's KPI Metrics tabs are absent by design — Stage 8
 * resultsets are served by `kpiBubble*` server functions behind Supabase auth,
 * and no equivalent exists on the bearer admin API EB uses.
 */
const ExecBridgeFlow: React.FC = () => {
  const { accountBriefings, payloads } = useAppSelector(s => s.xfactorViews);

  const [selected, setSelected] = React.useState<string | null>(null);
  const [step, setStep] = React.useState(0);
  const [modal, setModal] = React.useState<
    | null
    | "bubble"
    | "ro"
    | "cards"
    | "profiles"
    | "full"
    | "explain"
    | "section"
  >(null);
  const [sectionId, setSectionId] = React.useState("summary");
  /** Set when a matrix bubble opens a section for an account other than the
   *  one currently in the flow, so the modal reads the right payload. */
  const [sectionDigestId, setSectionDigestId] = React.useState<string | null>(
    null
  );

  const accounts = accountBriefings.accounts;

  const account: XFactorPortfolioAccount | null = React.useMemo(
    () => accounts.find(a => a.digest_id === selected) || null,
    [accounts, selected]
  );

  const payloadFor = (digestId: string | null | undefined) =>
    digestId ? (payloads[digestId]?.data?.data ?? null) : null;

  const data = payloadFor(account?.digest_id);
  const payloadState = account ? payloads[account.digest_id] : undefined;

  const anyLoading = accountBriefings.loading;

  const openAccount = (a: XFactorPortfolioAccount) => {
    setSelected(a.digest_id);
    setStep(0);
  };

  const stepAccount = (delta: number) => {
    if (!account) return;
    const i = accounts.findIndex(a => a.digest_id === account.digest_id);
    const next = accounts[i + delta];
    if (next) {
      setSelected(next.digest_id);
      setStep(0);
    }
  };

  const openSection = (id: string, digestId?: string) => {
    setSectionId(id);
    setSectionDigestId(digestId ?? null);
    setModal("section");
  };

  const sectionData = payloadFor(sectionDigestId ?? account?.digest_id);
  const sectionAccountName =
    (sectionDigestId
      ? accounts.find(a => a.digest_id === sectionDigestId)?.account_name
      : account?.account_name) || "Unknown account";
  const sectionDigestDate =
    (sectionDigestId
      ? accounts.find(a => a.digest_id === sectionDigestId)?.digest_date
      : account?.digest_date) || null;

  const unmapped = React.useMemo(() => unmappedKeys(data), [data]);

  /* ---------------------------------------------------------------- */

  if (!account) {
    return (
      <>
        <AccountsPortfolio
          accounts={accounts}
          loading={accountBriefings.loading}
          onOpenAccount={openAccount}
          onOpenBubble={() => setModal("bubble")}
          onOpenRo={() => setModal("ro")}
        />
        <BubbleMatrixModal
          open={modal === "bubble"}
          onClose={() => setModal(null)}
          accounts={accounts}
          payloads={payloads}
          loading={
            anyLoading && !accounts.every(a => payloads[a.digest_id]?.data)
          }
          onOpenBubble={(digestId, id) => openSection(id, digestId)}
        />
        <RoChartModal
          open={modal === "ro"}
          onClose={() => setModal(null)}
          accounts={accounts}
          onSelect={digestId => {
            setModal(null);
            const target = accounts.find(a => a.digest_id === digestId);
            if (target) openAccount(target);
          }}
        />
        <SectionDetailModal
          open={modal === "section"}
          onClose={() => setModal(null)}
          accountName={sectionAccountName}
          digestDate={sectionDigestDate}
          data={sectionData}
          sectionId={sectionId}
          onNavigate={setSectionId}
        />
      </>
    );
  }

  const accountIndex = accounts.findIndex(
    a => a.digest_id === account.digest_id
  );

  return (
    <div>
      {/* Flow header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {STEPS[step]} —{" "}
            <span style={{ color: ACCENT }}>
              {account.account_name || "Unknown account"}
            </span>
          </h2>
          <div className="mt-0.5 flex flex-wrap items-center text-xs text-gray-500">
            <span>Public {formatDate(account.digest_date) || "—"}</span>
            {account.is_stale ? <StatusBadge kind="stale" /> : null}
            {account.is_partial ? (
              <PartialStatus missingSectionIds={account.missing_section_ids} />
            ) : null}
            <span className="mx-2 text-gray-300">·</span>
            <span>
              Step {step + 1} of {STEPS.length}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">Account Briefing:</span>
          <button
            type="button"
            disabled={accountIndex <= 0}
            onClick={() => stepAccount(-1)}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-600 transition-colors enabled:hover:bg-gray-100 disabled:opacity-30"
          >
            <ChevronLeft size={13} /> previous
          </button>
          <button
            type="button"
            disabled={accountIndex >= accounts.length - 1}
            onClick={() => stepAccount(1)}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-600 transition-colors enabled:hover:bg-gray-100 disabled:opacity-30"
          >
            next <ChevronRight size={13} />
          </button>

          <button
            type="button"
            onClick={() => setSelected(null)}
            className="rounded px-2 py-1 text-xs font-medium transition-colors hover:bg-purple-50"
            style={{ color: ACCENT }}
          >
            ← Portfolio
          </button>

          {step > 0 ? (
            <button
              type="button"
              onClick={() => setStep(s => s - 1)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              <ArrowLeft size={13} /> {STEPS[step - 1]}
            </button>
          ) : null}
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep(s => s + 1)}
              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#111827" }}
            >
              {STEPS[step + 1]} <ArrowRight size={13} />
            </button>
          ) : null}
        </div>
      </div>

      {payloadState?.loading && !data ? (
        <p className="py-10 text-center text-sm text-gray-400">
          Loading briefing…
        </p>
      ) : payloadState?.error && !data ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {payloadState.error}
        </div>
      ) : (
        <>
          {unmapped.length ? (
            <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
              <span className="font-medium">
                Payload keys this port does not read:
              </span>{" "}
              {unmapped.join(", ")} — xfactor publishes no schema, so a new or
              renamed key shows up here rather than being silently dropped.
            </div>
          ) : null}

          {step === 0 ? (
            <SituationCard
              accountName={account.account_name || "Unknown account"}
              data={data}
            />
          ) : null}
          {step === 1 ? (
            <ExecutiveBrief
              accountName={account.account_name || "Unknown account"}
              data={data}
            />
          ) : null}
          {step === 2 ? (
            <AccountSynopsis
              accountName={account.account_name || "Unknown account"}
              data={data}
              onOpenSection={id => openSection(id)}
              onOpenCards={() => setModal("cards")}
              onOpenProfiles={() => setModal("profiles")}
              onOpenFullBriefing={() => setModal("full")}
              onOpenExplain={() => setModal("explain")}
            />
          ) : null}
        </>
      )}

      <SectionDetailModal
        open={modal === "section"}
        onClose={() => setModal(null)}
        accountName={sectionAccountName}
        digestDate={sectionDigestDate}
        data={sectionData}
        sectionId={sectionId}
        onNavigate={setSectionId}
      />
      <BriefingCardsModal
        open={modal === "cards"}
        onClose={() => setModal(null)}
        accountName={account.account_name || "Unknown account"}
        digestDate={account.digest_date}
        data={data}
      />
      <ExecProfilesModal
        open={modal === "profiles"}
        onClose={() => setModal(null)}
        accountName={account.account_name || "Unknown account"}
        digestDate={account.digest_date}
        data={data}
      />
      <FullBriefingModal
        open={modal === "full"}
        onClose={() => setModal(null)}
        accountName={account.account_name || "Unknown account"}
        digestDate={account.digest_date}
        data={data}
      />
      <ExplainabilityModal
        open={modal === "explain"}
        onClose={() => setModal(null)}
        accountName={account.account_name || "Unknown account"}
        digestDate={account.digest_date}
        data={data}
      />
    </div>
  );
};

export default ExecBridgeFlow;
